import _ from 'lodash';
import React, { useState } from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { Button } from 'antd';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import SpinnerError from '$components/library/SpinnerError';
import { humanizeMs } from '$lib/format';
import { Query } from '@apollo/react-components';
import { gql } from '@apollo/client';
import styles from './styles.scss';

const ADMIN_PRODUCT_STATS = gql`
  query AdminStats {
    adminStats {
      stats
    }
  }
`;

const colorMap = {
  achievable: '#CECECE',
  wso: '#9AAAF7',
  'basic-wisdom': '#3CB4F7',
};

const Card = (props) => {
  const { children, title } = props;

  return (
    <div className={styles.cardLayout}>
      <div className={styles.cardTitle}>{title}</div>
      <div className={styles.cardContent}>{children}</div>
    </div>
  );
};

const AdminStats = () => {
  const [showStats, setShowStats] = useState(false);

  if (!showStats) {
    return (
      <div>
        <Button onClick={() => setShowStats(true)}>Show stats</Button>
      </div>
    );
  }

  return (
    <Query query={ADMIN_PRODUCT_STATS}>
      {({ loading, error, data }) => {
        if (loading || error) {
          return <SpinnerError error={error} />;
        }

        const { adminStats } = data;

        const { stats } = adminStats;
        const { debug, totals, byDay } = stats;
        const {
          totalContentMs,
          totalPresentations,
          totalReviewMs,
          totalSubscriptions,
          totalUsers,
        } = totals;

        const { now } = debug;

        const uniqueSources = _.keys(
          _.reduce(
            byDay,
            (acc, obj, _key) => {
              const { signUpSources } = obj;

              _.keys(signUpSources).forEach((source) => {
                acc[source] = true;
              });

              return acc;
            },
            {}
          )
        );

        const signUpSourcesByDay = _.reduce(
          byDay,
          (acc, obj, key) => {
            const { signUpSources } = obj;

            const datum = {
              day: key,
            };

            _.forEach(uniqueSources, (source) => {
              datum[source] = signUpSources[source] || 0;
            });
            acc.push(datum);
            return acc;
          },
          []
        );

        const contentByDay = _.reduce(
          byDay,
          (acc, obj, key) => {
            const { contentCompleted, presentations } = obj;
            const datum = {
              day: key,
              contentCompleted,
              presentations,
            };
            acc.push(datum);
            return acc;
          },
          []
        );

        const reviewMsByDay = _.reduce(
          byDay,
          (acc, obj, key) => {
            const { reviewMs } = obj;
            const datum = {
              day: key,
              reviewMs,
            };
            acc.push(datum);
            return acc;
          },
          []
        );

        return (
          <div className={styles.cards}>
            <Card title="Overall stats">
              <div>Total review time: {humanizeMs(totalReviewMs)}</div>
              <div>Total reading time: {humanizeMs(totalContentMs)}</div>
              <div>Total presentations: {numeral(totalPresentations).format('0,0')}</div>
              <div>Total subscriptions: {numeral(totalSubscriptions).format('0,0')}</div>
              <div>Total signups: {numeral(totalUsers).format('0,0')}</div>
              <div>Generated at {now}</div>
            </Card>
            <Card title="Sign up sources by day">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart width={300} height={200} data={signUpSourcesByDay}>
                  <XAxis dataKey="day" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  {uniqueSources.map((source) => {
                    return (
                      <Area
                        baseValue="0"
                        key={source}
                        type="step"
                        dataKey={source}
                        stackId="day"
                        stroke={colorMap[source]}
                        fill={colorMap[source]}
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Review time by day">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={reviewMsByDay}>
                  <XAxis dataKey="day" />
                  <YAxis
                    tickFormatter={(val) => {
                      return `${moment.duration(val).asHours().toFixed(2)}hrs`;
                    }}
                  />
                  <Tooltip
                    formatter={(value, _name, _props) => {
                      if (!value) {
                        return 0;
                      }

                      const humanized = moment.duration(value).humanize();
                      return `${value} (about ${humanized})`;
                    }}
                  />
                  <Line type="monotone" dataKey="reviewMs" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Content by day">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart width={300} height={200} data={contentByDay}>
                  <XAxis dataKey="day" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="contentCompleted"
                    stroke="#8884d8"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="presentations"
                    stroke="#82ca9d"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>
        );
      }}
    </Query>
  );
};

export default AdminStats;
